import { KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';

import { RiArrowLeftSLine } from 'react-icons/ri';
import { Countdown } from 'components/countdown';
import { ExportButton } from 'components/generate-xcel';
import ModalChangeIndicator from 'components/modal-change-indicator';
import CartoesCarregando from 'components/ui/cartoes-carregando';
import { NotFound } from 'pages/NotFound';
import { etapasDoCiclo } from 'pages/MinhaEquipe/calendario-do-ciclo';
import CalendarioDoCiclo from 'pages/MinhaEquipe/components/CalendarioDoCiclo';
import { periodoDa } from 'pages/RealizacaoDestaque/components/AgendaDaRealizacao';
import CartaoDaFila from 'pages/RealizacaoDestaque/components/CartaoDaFila';
import { IndicadorDaFila } from 'pages/RealizacaoDestaque/components/IndicadoresDaFila';
import SecaoDaFila, { CLASSES_DO_EXPORTAR } from 'pages/RealizacaoDestaque/components/SecaoDaFila';
import SeletoresDaFila from 'pages/RealizacaoDestaque/components/SeletoresDaFila';
import TopoDoGestor from 'pages/RealizacaoDestaque/components/TopoDoGestor';
import VazioDaFila from 'pages/RealizacaoDestaque/components/VazioDaFila';
import { OrdemDaFila, ordenarFila } from 'pages/RealizacaoDestaque/ordenar-fila';
import { getTasksManagerMad } from 'services/dashboard.service';
import { GetVersion } from 'services/utils';
import { getFutureCalendar, getMonthNameFromString } from 'utils/helpers';
import { CheckManagerType } from 'utils/strings';

import ModalAlert from '../../components/ModalAlert';
import ModalCod5 from '../../components/ModalCod5';
import SelectDupla from './SelectDupla';

import { setFlowChoice } from '../shared/eligibility';
import { FlowLoading } from '../shared/FlowLoading';
import { canUpdateIndicator } from '../shared/indicator';
import { getPlayerInfoWithPairs } from '../shared/playerInfoCache';
import { FlowTabs } from '../shared/FlowTabs';
import { matchesSearch } from '../shared/search';
import { useCycleStage } from '../shared/useCycleStage';
import { useFlowEligibility } from '../shared/useFlowEligibility';
import { useLateralidade } from '../shared/useLateralidade';
import { useServerTime } from 'hooks/useServerTime';
import { useTasksData } from '../shared/useTasksData';
import { buildMadContext, MadRuleContext, madUserCodEtapa } from './context';
import { computeQuotas, filterStageData } from './quotas';
import { optionsPairs, filterByPair } from './pairs';
import {
  canApproveParticipantsCount,
  canAward,
  canDouble,
  canEdit,
  canEvaluate,
  canPromote,
  canReset,
  getPlayerStep,
  getTaskStatuses,
  getTaskStepLocal,
  hasTaskFeedbackForUser,
  isPitStopAssessor,
} from './rules';
import { useMadActions } from './useMadActions';

// Página do fluxo MAD (dupla de liderança) — espelho de normal/index.tsx restrito ao
// universo exclusivamente MAD (aggregate tasks_manager_mad). Além das abas Diretoria |
// MAD | Rede (FlowTabs), o MAD tem o SELETOR DE PAR da dupla (COT-03); a divisão
// Normal×MAD é feita pelas rotas (ELE-02/04) e MAD é a página atual, então as abas
// Diretoria e Rede navegam para o fluxo Normal.

export function FluxoMAD() {
  const location = useLocation();

  const { user, cyclesExtended, realCycle, cycleSelected, setCycleSelected, userInfo, cycles } = useAuth();

  const { realDatetime } = useServerTime();
  const { internalUser, isVacationManager } = useLateralidade(user);

  // Fase trazida pelo FlowTabs na troca de fluxo. Capturada na MONTAGEM: logo depois a
  // página re-navega para a própria rota (efeito de troca de ciclo), zerando location.state.
  const [requestedStage] = useState<string | undefined>(() => (location.state as { stage?: string } | null)?.stage);

  // Seletor de par da dupla (COT-03) — substitui as tabs diretoria×rede do Normal.
  // undefined/próprio = minhas realizações; parceiro = as realizações que ele tocou.
  const [pairSelected, setPairSelected] = useState<string | undefined>();

  const [pageLoading, setPageLoading] = useState(false);
  const [gestor, setIsGestor] = useState(false);
  const [playerInfo, setPlayerInfo] = useState<any>();

  const [filteredData, setFilteredData] = useState<any[]>();
  const [tasksByFilter, setTasksByFilter] = useState<any[]>();
  const [tasksStatus, setTasksStatus] = useState<string[]>([]);
  const [selectedtasksStatus, setSelectedTasksStatus] = useState<string>();
  const [selectedFeedbackStatus, setSelectedFeedbackStatus] = useState<string>();
  const [searchText, setSearchText] = useState<string>();
  const [showConcurrentActionModal, setShowConcurrentActionModal] = useState<string | undefined>();
  // Realização com o modal de ajuste de indicador aberto. Guarda o `owner` junto porque
  // a lista de indicadores é a do DONO, não a do gestor que está com o modal aberto.
  const [indicatorTask, setIndicatorTask] = useState<{ id: string; owner?: string } | undefined>();

  // Ordem do seletor "Ordenar por" (RDN-49): só apresentação, aplicada sobre tasksByFilter na renderização.
  const [ordem, setOrdem] = useState<OrdemDaFila>('atual');

  const [showMore, setShowMore] = useState(false);
  const [calendarElementsIndexOf] = useState<number>(5);
  const [calendarNumberOfElements, setCalendarNumberOfElements] = useState(5);
  const [calendarShowMore, setCalendarShowMore] = useState(false);

  // PARIDADE: o bônus "Carta Reconhecer+" nunca é carregado no monólito (verifyBonus só
  // vive em código comentado). Mantemos indefinido para não mudar comportamento.
  const [bonus] = useState<any>(undefined);

  const { data: rawData, loading, setLoading, reload } = useTasksData(getTasksManagerMad);

  // Nível efetivo p/ calendário — no MAD a composição pela dupla é integral (madUserCodEtapa).
  const codEtapaEfetivo = useMemo(() => {
    if (!playerInfo) return undefined;

    return madUserCodEtapa(buildMadContext({ playerInfo, isVacationManager }), playerInfo);
  }, [isVacationManager, playerInfo]);

  const {
    stages,
    activeStageObj,
    getActiveEvaluationStep,
    currentCycle,
    setCurrentCycle,
    currentStage,
    setCurrentStage,
    optionsCycle,
    activeCycleSelected,
    setActiveCycleSelected,
    elementsIndexOf,
    numberOfElements,
    setNumberOfElements,
    configCycleAndStage,
  } = useCycleStage({
    cyclesExtended: cyclesExtended as any,
    cycleSelected,
    realDatetime,
    codEtapaEfetivo,
    requestedStage,
  });

  // Elegibilidade Normal×MAD do gestor exibido NO CICLO SELECIONADO — alimenta as abas
  // (ELE-04) e tira o gestor daqui se o ciclo escolhido não tiver universo MAD.
  const eligibility = useFlowEligibility({
    playerId: internalUser?._id,
    cycleId: cycleSelected?._id,
    tasks: rawData,
    flow: 'mad',
    currentStage,
  });

  // Com as abas de volta para dentro do card (e não mais no submenu do header), o card
  // precisa existir sempre que houver fluxo para trocar — senão o gestor elegível aos
  // dois ficaria preso neste fluxo fora da janela de apreciação.
  const canSwitchFlow = !!eligibility?.normal && !!eligibility?.mad;

  // Contexto-base das regras (sem cotas) — espelha os estados homônimos do monólito.
  const baseCtx: MadRuleContext = useMemo(
    () =>
      buildMadContext({
        playerInfo,
        userInfo: userInfo as any,
        internalUser: internalUser as any,
        realDatetime,
        activeStageObj: activeStageObj as any,
        getActiveEvaluationStep,
        cyclesExtended: cyclesExtended as any,
        realCycle: realCycle as any,
        isVacationManager,
        bonus,
      }),
    [
      activeStageObj,
      bonus,
      cyclesExtended,
      getActiveEvaluationStep,
      internalUser,
      isVacationManager,
      playerInfo,
      realCycle,
      realDatetime,
      userInfo,
    ],
  );

  // Enriquecimento com labels de status (UI-08).
  const data = useMemo(() => {
    if (!Array.isArray(rawData)) return rawData;

    return rawData.map((task: any) => ({
      ...task,
      statuses: getTaskStatuses(baseCtx, task) || [],
    }));
  }, [baseCtx, rawData]);

  // Port de getUserInfo (index.tsx:2058-2072): playerInfo do gestor exibido, com pares.
  // Cacheado por sessão (playerInfoCache): a troca de aba remonta o fluxo e a guarda de
  // render não deve esperar rede de novo pelo mesmo gestor.
  const getUserInfo = useCallback(async () => {
    try {
      if (playerInfo && playerInfo.chave === internalUser?._id) return;
      if (!internalUser?._id) return;

      const playerInfoResult = await getPlayerInfoWithPairs(internalUser._id);

      if (playerInfoResult) {
        setIsGestor(playerInfoResult.is_manager);
        setPlayerInfo(playerInfoResult);
        setLoading(false);
      }
    } catch (e) {
      console.log(e);
    }
  }, [internalUser?._id, playerInfo, setLoading]);

  // Port do loadData (index.tsx:2085-2148) com os parâmetros da fase fechados.
  const loadData = useCallback(
    async (taskId?: string) => {
      if (!!activeStageObj) {
        const hasTaskId = typeof taskId === 'string' && taskId.length > 0;

        if (!hasTaskId) {
          setTasksStatus([]);
          setSelectedFeedbackStatus(undefined);
          setSelectedTasksStatus(undefined);
          setSearchText(undefined);
        }

        return reload(
          {
            player: internalUser?.extra.id,
            start: activeStageObj.date_start,
            end: activeStageObj.date_finish,
            step: (activeStageObj as any).step,
          },
          taskId,
        );
      }

      return typeof taskId === 'string' && taskId.length > 0 ? null : [];
    },
    [activeStageObj, internalUser?.extra?.id, reload],
  );

  // Port do memo isDirectManager (index.tsx:139-147).
  const isDirectManager = useMemo(
    () =>
      isVacationManager &&
      userInfo &&
      playerInfo &&
      ((userInfo as any)?._id === playerInfo?.gestor ||
        (Array.isArray((userInfo as any)?.mad_dupla) &&
          (userInfo as any)?.mad_dupla?.some((md: any) => md._id && md._id === playerInfo?.gestor))),
    [isVacationManager, playerInfo, userInfo],
  );

  // Port de index.tsx:699-708.
  const isOriginalManager = useMemo(() => {
    if (!filteredData?.length) return false;
    return !!filteredData.find(
      x =>
        x.original_manager &&
        (x.original_manager === playerInfo?._id ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(x.original_manager)))),
    );
  }, [filteredData, playerInfo]);

  // Port de index.tsx:710-721.
  const isOriginalManagerCod5 = useMemo(() => {
    if (!filteredData?.length || !playerInfo) return false;
    return !!filteredData.find(
      x =>
        x.original_manager &&
        (x.original_manager === playerInfo?._id ||
          (Array.isArray(playerInfo?.mad_dupla) &&
            playerInfo.mad_dupla.some((md: any) => md._id && md._id === String(x.original_manager)))) &&
        madUserCodEtapa(baseCtx) &&
        madUserCodEtapa(baseCtx) === 5,
    );
  }, [baseCtx, filteredData, playerInfo]);

  // Port de index.tsx:723-728.
  const handleLinkSpaceKeyDown = useCallback((event: KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      event.currentTarget.click();
    }
  }, []);

  // Opções do seletor de par (COT-03), cada uma com a quantidade de realizações da fase daquele par: o mesmo
  // `filterByPair` que monta a lista, sobre os dados já carregados (sem requisição nova).
  // Roda na renderização: se a contagem lançar erro, as opções ficam só com o nome (RDV-40), como o efeito que monta
  // a lista cai para a lista vazia.
  const pairOptions = useMemo(() => {
    const opcoes = optionsPairs(playerInfo);
    try {
      const daFase = (playerInfo && filterStageData(baseCtx, data)) || [];
      return opcoes.map(option => ({
        label: `${option.label ?? ''} · ${(filterByPair(daFase, option.value, baseCtx) || []).length}`,
        value: option.value,
      }));
    } catch {
      return opcoes.map(option => ({ label: option.label ?? '', value: option.value }));
    }
  }, [playerInfo, baseCtx, data]);

  // Port do clarifyingText (index.tsx:753-933) — madUserCodEtapa (composição integral).
  const clarifyingText = useMemo(() => {
    if (
      !playerInfo ||
      !Array.isArray(filteredData) ||
      !madUserCodEtapa(baseCtx) ||
      typeof madUserCodEtapa(baseCtx) !== 'number' ||
      (madUserCodEtapa(baseCtx) as number) < 1 ||
      (madUserCodEtapa(baseCtx) as number) > 5
    )
      return null;

    const _tasks = [...(filteredData as any[])];
    let stepMsg;

    if (madUserCodEtapa(baseCtx) === 4 && !!isOriginalManager) {
      if (typeof getActiveEvaluationStep === 'number') {
        if (getActiveEvaluationStep === 2) {
          stepMsg = 0;
        } else if (getActiveEvaluationStep === 3) {
          stepMsg = 1;
        } else {
          stepMsg = 2;
        }
      } else {
        stepMsg = 2;
      }
    } else if (madUserCodEtapa(baseCtx) === 5 && !!isOriginalManager) {
      if (typeof getActiveEvaluationStep === 'number') {
        if (getActiveEvaluationStep === 2) {
          stepMsg = 0;
        } else if (getActiveEvaluationStep === 3) {
          stepMsg = 1;
        } else if (getActiveEvaluationStep === 4 || getActiveEvaluationStep === 4.1) {
          stepMsg = 2;
        } else {
          stepMsg = 3;
        }
      } else {
        stepMsg = 3;
      }
    } else {
      if (madUserCodEtapa(baseCtx) === 2) {
        stepMsg = 0;
      } else if (madUserCodEtapa(baseCtx) === 4) {
        stepMsg = 2;
      } else if (madUserCodEtapa(baseCtx) === 5) {
        stepMsg = 3;
      } else if (madUserCodEtapa(baseCtx) === 1 || madUserCodEtapa(baseCtx) === 3) {
        if (_tasks.length) {
          let hasLevel1 = false;

          _tasks.forEach(task => {
            if (
              typeof getTaskStepLocal(task) === 'number' &&
              (getTaskStepLocal(task) as number) >= 3 &&
              ((typeof getActiveEvaluationStep === 'number' && getActiveEvaluationStep > 2) || getActiveEvaluationStep === null)
            ) {
              hasLevel1 = true;
            }
          });

          stepMsg = hasLevel1 ? 1 : 0;
        } else {
          stepMsg = 0;
        }
      }
    }

    if (typeof stepMsg === 'number') {
      if (stepMsg === 1) {
        return (
          <>
            <p className="m-0 mb-1.5 font-bold text-brand-blue-dark">
              Apreciação das realizações validadas
            </p>
            <p className="m-0 mb-1">
              Cada realização recebida por você foi <strong className="font-bold">validada</strong> por funcionário pertencente à
              função gerencial e recebeu 300 (trezentos) pontos.
            </p>
            <p className="m-0 mb-1">
              Nesta apreciação, você poderá BONIFICAR todas as realizações que considerar mais relevantes em seu âmbito de
              atuação, e PROMOVER ao próximo nível, até 10% (dez por cento) das realizações bonificadas que você considerar
              extraordinárias.
            </p>
            <p className="m-0 mb-1">
              A realização <strong className="font-bold">bonificada</strong> passará a 600 (seiscentos) pontos e a{' '}
              <strong className="font-bold">promovida</strong> terá 100 (cem) pontos adicionais.
            </p>

            <p className="m-0">
              <b className="text-brand-blue-dark">Lembre-se</b> que ao deixar feedback para{' '}
              <strong className="font-bold">as entregas</strong> recebidas você promove o engajamento e o desenvolvimento de sua
              equipe.
            </p>
          </>
        );
      } else if (stepMsg === 2) {
        return (
          <>
            <p className="m-0 mb-1.5 font-bold text-brand-blue-dark">
              Apreciação das entregas promovidas pelo nível 1
            </p>
            <p className="m-0 mb-1">
              Cada realização recebida por você foi <b>promovida</b> por funcionário pertencente à função gerencial e recebeu 700 (setecentos) pontos.
            </p>
            <p className="m-0 mb-1">
              Nesta apreciação, você poderá BONIFICAR todas as realizações que considerar relevantes em seu âmbito de atuação, e PROMOVER ao próximo nível, até 20% (vinte por cento) das realizações bonificadas que você considerar extraordinárias.
            </p>
            <p className="m-0 mb-1">
              A realização <strong className="font-bold">bonificada</strong> passará a 1200 (mil e duzentos) pontos e a <strong className="font-bold">promovida</strong> terá 100 (cem) pontos adicionais.
            </p>

            <p className="m-0">
            Lembre-se que ao deixar feedback para as entregas recebidas você promove o engajamento e o desenvolvimento de sua equipe.
            </p>
          </>
        );
      } else if (stepMsg === 3) {
        return (
          <>
            <p className="m-0 mb-1.5 font-bold text-brand-blue-dark">
              Apreciação das entregas promovidas pelo nível 2
            </p>
            <p className="m-0 mb-1">
              Cada realização recebida por você foi promovida por funcionário responsável pela apreciação nível 2 e recebeu 1.300
              (mil e trezentos) pontos.
            </p>
            <p className="m-0 mb-1">
              Nesta apreciação, você poderá BONIFICAR VALEU para 100% das realizações, BONIFICAR MANDOU BEM para até 50%
              (cinquenta por cento) das realizações e BONIFICAR UAU para até 25% (vinte e cinco por cento) das realizações que
              você considerar extraordinárias.
            </p>
            <p className="m-0 mb-1">
              A realização <strong className="font-bold">Bonificada Valeu</strong> passará a 1.500 (mil e quinhentos) pontos, a
              realização <strong className="font-bold">Bonificada Mandou Bem</strong> passará a 2.500 (dois mil e quinhentos)
              pontos e a realização <strong className="font-bold">Bonificada UAU</strong> passará a 5.000 (cinco mil) pontos.
            </p>

            <p className="m-0">
              E que tal deixar um feedback para a equipe? Afinal, reconhecimento nunca é demais!
            </p>
          </>
        );
      } else {
        return (
          <>
            <p className="m-0 mb-1.5 font-bold text-brand-blue-dark">
              VALIDAÇÃO das entregas cadastradas por sua equipe
            </p>
            <p className="m-0 mb-1">
              Cada realização recebida por você foi cadastrada por um de seus liderados.
            </p>
            <p className="m-0 mb-1">
              Para VALIDAR a entrega, verifique se há alinhamento com os indicadores e desafios da equipe, se não há informações
              sigilosas, duplicidade e ou conteúdo inadequado, caso contrário você pode DEVOLVER a realização.
            </p>
            <p className="m-0 mb-1">
              Ao ser devolvida, a realização volta para o responsável pelo cadastramento e se for corrigida, dentro do período de
              validação, você poderá analisá-la novamente.
            </p>
            <p className="m-0 mb-1">
              A realização <strong className="font-bold">validada</strong> recebe 300 (trezentos) pontos.
            </p>

            <p className="m-0">
              <b className="text-brand-blue-dark">Lembre-se</b> que ao deixar feedback para{' '}
              <strong className="font-bold">as entregas</strong> recebidas você promove o engajamento e o desenvolvimento de sua
              equipe.
            </p>
          </>
        );
      }
    } else {
      return null;
    }
  }, [baseCtx, filteredData, getActiveEvaluationStep, isOriginalManager, playerInfo]);

  // Port de index.tsx:948-960.
  const getFullCalendar = useMemo(() => {
    if (!cyclesExtended || !cycleSelected) return null;

    const cycleActive = cyclesExtended.find(x => x._id === cycleSelected?._id);

    if (cycleActive) {
      const calendar = getFutureCalendar(cycleActive?.steps, realDatetime);
      return calendar;
    }

    return null;
  }, [cycleSelected, cyclesExtended, realDatetime]);

  // Port de hasRedeMAD (index.tsx:3156-3178) — no MAD a flag é usada só para gate de
  // exibição do painel de cotas (as tabs diretoria/rede não existem aqui).
  const hasRedeMAD: { rede: boolean; mad: boolean } | undefined = useMemo(() => {
    if (!Array.isArray(data) || !data.length || !playerInfo || !activeStageObj) return undefined;

    const obj = { rede: false, mad: false };

    const itemsByStage = data?.filter(item => {
      return item.created >= (activeStageObj as any)?.date_start && item.created <= (activeStageObj as any)?.date_finish;
    });

    if (itemsByStage.length) {
      if (
        madUserCodEtapa(baseCtx) &&
        madUserCodEtapa(baseCtx) === 5 &&
        (activeStageObj as any)?.type &&
        (activeStageObj as any)?.type === 'semester'
      ) {
        obj.rede = !!itemsByStage.filter(
          (x: { prosseguir_gst: string; owner_tipo_classificacao: string }) =>
            (!!x.prosseguir_gst && x.prosseguir_gst === 'approved') ||
            (!!x.owner_tipo_classificacao && x.owner_tipo_classificacao === 'UNIDADE TÁTICA'),
        ).length;
      }

      obj.mad = !!itemsByStage.filter(x => !!x.mad || (Array.isArray(x.non_evaluator) && x.non_evaluator.length > 0)).length;
    }

    return obj;
  }, [activeStageObj, baseCtx, data, playerInfo]);

  // Port de tasksProsseguirAssessor (index.tsx:3142-3154) — dupla-aware: a task parada
  // comigo OU com meu parceiro (a dupla age como um).
  const tasksProsseguirAssessor: any[] = useMemo((): any[] => {
    if (!Array.isArray(filteredData) || !filteredData.length || !playerInfo) return [];

    const duplaIds = Array.isArray(playerInfo?.mad_dupla)
      ? playerInfo.mad_dupla
          .map((md: any) => md?._id)
          .filter(Boolean)
          .map(String)
      : [];

    const tasks = filteredData.filter(task => {
      if (
        !!task.prosseguir_idGestor &&
        (task.prosseguir_idGestor === playerInfo._id || duplaIds.includes(String(task.prosseguir_idGestor)))
      )
        return true;

      if (
        Array.isArray(task.prosseguir_managers) &&
        (task.prosseguir_managers.includes(playerInfo._id) ||
          task.prosseguir_managers.some((id: string) => duplaIds.includes(String(id))))
      )
        return true;

      return false;
    });

    return tasks;
  }, [filteredData, playerInfo]);

  // Universo por fase + par (COT-01/COT-03): filterStageData (pool MAD) então filterByPair.
  useEffect(() => {
    function updateData() {
      try {
        let stageData: any[] = filterStageData(baseCtx, data);

        stageData = filterByPair(stageData, pairSelected, baseCtx);

        const statuses: string[] = ['Selecione'];

        stageData.forEach((task: any) => {
          if (Array.isArray(task.statuses) && task.statuses.length) {
            task.statuses.forEach((_status: string) => {
              if (!statuses.find(x => x === _status)) {
                statuses.push(_status);
              }
            });
          }
        });

        setTasksStatus(statuses);

        setFilteredData(stageData);

        setLoading(false);
      } catch (e) {
        setFilteredData([]);
        setLoading(false);
      }
    }

    if (playerInfo) updateData();
  }, [pairSelected, baseCtx, data, playerInfo, setLoading]);

  // Cotas e universos da fase (COT-04..09) sobre o pool MAD filtrado por par.
  const quotas = useMemo(() => computeQuotas(baseCtx, Array.isArray(filteredData) ? filteredData : []), [baseCtx, filteredData]);

  // Contexto COMPLETO das regras: base + cotas.
  const ctx: MadRuleContext = useMemo(
    () => ({
      ...baseCtx,
      doubledTasks: quotas.doubledTasks,
      maxDoubledTasks: quotas.maxDoubledTasks,
      promotedTasks: quotas.promotedTasks,
      maxPromotedTasks: quotas.maxPromotedTasks,
      uauTasks: quotas.uauTasks,
      maxUauTasks: quotas.maxUauTasks,
      mandouBemTasks: quotas.mandouBemTasks,
      maxMandouBemTasks: quotas.maxMandouBemTasks,
    }),
    [baseCtx, quotas],
  );

  const {
    selectedDupla,
    setSelectedDupla,
    functionDupla,
    getAction,
    setGetAction,
    modalCod5,
    setModalCod5,
    handleAccept,
    handleStop,
    handleReject,
    handleDoubled,
    handlePromote,
    handleAward,
    handleReset,
    handleSubmitCod5,
    resolveDuplaSelection,
  } = useMadActions({
    ctx,
    data,
    filteredData,
    reload: loadData,
    setPageLoading,
    showConcurrentModal: setShowConcurrentActionModal,
    quotas,
    bonus,
    user,
    isVacationManager,
  });

  const functionsObject: any = {
    handleAccept,
    handlePromote,
  };

  // Filtro por feedback/status/texto (port de index.tsx:3966-3998, UI-05).
  useEffect(() => {
    if (Array.isArray(filteredData) && filteredData.length) {
      let _data = Array.isArray(filteredData) && filteredData.length ? filteredData : [];

      if (
        typeof selectedFeedbackStatus === 'string' &&
        selectedFeedbackStatus.length &&
        selectedFeedbackStatus.toLowerCase() !== 'selecione'
      ) {
        _data = _data.filter((task: any) => {
          const hasFeedback = hasTaskFeedbackForUser(baseCtx, task);
          return selectedFeedbackStatus === 'sim' ? hasFeedback : !hasFeedback;
        });
      }

      if (
        typeof selectedtasksStatus === 'string' &&
        selectedtasksStatus.length &&
        selectedtasksStatus.toLowerCase() !== 'selecione'
      ) {
        _data = _data.filter(x => !!x.statuses.includes(selectedtasksStatus));
      }

      if (typeof searchText === 'string' && searchText.trim().length) {
        const _search = searchText.replace(/ +/gi, ' ').trim().toLowerCase();
        _data = _data.filter(x => matchesSearch(x, _search));
      }

      setTasksByFilter(_data);
    } else {
      setTasksByFilter([]);
    }
  }, [baseCtx, filteredData, searchText, selectedFeedbackStatus, selectedtasksStatus]);

  // A mesma contagem alimenta as opções do filtro e o indicador "Com feedback" (RDV-30).
  const contagemDeFeedback = useMemo(() => {
    const tasks = Array.isArray(filteredData) ? filteredData : [];
    return { comFeedback: tasks.filter(task => hasTaskFeedbackForUser(baseCtx, task)).length, total: tasks.length };
  }, [baseCtx, filteredData]);

  const feedbackFilterOptions = useMemo(() => {
    const withFeedback = contagemDeFeedback.comFeedback;
    const withoutFeedback = contagemDeFeedback.total - withFeedback;

    return [
      {
        label: 'Selecione',
        value: 'selecione',
      },
      {
        label: `Com feedback (${withFeedback})`,
        value: 'sim',
      },
      {
        label: `Sem feedback (${withoutFeedback})`,
        value: 'nao',
      },
    ];
  }, [contagemDeFeedback]);

  const selectedFeedbackOption = useMemo(
    () => feedbackFilterOptions.find(option => option.value === selectedFeedbackStatus) || null,
    [feedbackFilterOptions, selectedFeedbackStatus],
  );

  const taskStatusOptions = useMemo(() => {
    const tasks = Array.isArray(filteredData) ? filteredData : [];

    return (
      tasksStatus?.map(status => ({
        label: `${status}${status !== 'Selecione' ? ` (${tasks.filter(x => !!x.statuses.includes(status)).length})` : ''}`,
        value: status,
      })) || []
    );
  }, [filteredData, tasksStatus]);

  const selectedTaskStatusOption = useMemo(
    () => taskStatusOptions.find(option => option.value === selectedtasksStatus) || null,
    [selectedtasksStatus, taskStatusOptions],
  );

  // Port de index.tsx:4042-4047.
  useEffect(() => {
    if (!internalUser && !playerInfo) return;

    setLoading(true);
    getUserInfo();
  }, [getUserInfo, internalUser, playerInfo, setLoading]);

  // Port de index.tsx:4049-4055.
  useEffect(() => {
    if (!gestor) return;

    setLoading(true);

    loadData();
  }, [gestor, loadData, setLoading]);

  // Port de index.tsx:4057-4065.
  useEffect(() => {
    if (!stages) return;

    if (stages.length <= numberOfElements) {
      setShowMore(false);
    } else {
      setShowMore(true);
    }
  }, [numberOfElements, stages]);

  // Port de index.tsx:4067-4075.
  useEffect(() => {
    if (!getFullCalendar) return;

    if (getFullCalendar.length <= calendarNumberOfElements) {
      setCalendarShowMore(false);
    } else {
      setCalendarShowMore(true);
    }
  }, [calendarNumberOfElements, getFullCalendar]);

  // Port de index.tsx:4077-4091 — recarrega ao voltar da tela de detalhe.
  useEffect(() => {
    try {
      const locationState: any = location.state;

      if (locationState && locationState.direction) {
        if (locationState.direction === 'back') {
          if (gestor) {
            loadData();
          }
        }
      }
    } catch (e) {
      console.log(e);
    }
  }, [gestor, loadData, location]);

  // Port do reload:lateralidade (index.tsx:4118-4122).
  useEffect(() => {
    function handleLateralidadeLoading() {
      setLoading(true);
    }

    document.addEventListener('reload:lateralidade', handleLateralidadeLoading, false);

    return () => {
      document.removeEventListener('reload:lateralidade', handleLateralidadeLoading);
    };
  }, [setLoading]);

  // Port de index.tsx:4207-4211.
  useEffect(() => {
    if (!user || !cyclesExtended || !internalUser) return;

    configCycleAndStage();
  }, [configCycleAndStage, cyclesExtended, internalUser, user]);

  // Estar AQUI é a escolha: grava a visita para o guarda-chuva voltar ao MAD nesta
  // sessão (ELE-02) — não só o clique no switcher conta.
  useEffect(() => {
    setFlowChoice('mad');
  }, []);

  function getIndexByValue(array: any, value: string) {
    return array.findIndex((element: any) => element.value === value);
  }

  // Guardas de render — port de index.tsx:4239-4277. Em vez de tela em branco (null),
  // renderiza o shell da página com spinner enquanto user/ciclo/fase resolvem.
  if (
    !user ||
    !internalUser ||
    typeof gestor !== 'boolean' ||
    typeof currentCycle === 'undefined' ||
    typeof currentStage === 'undefined' ||
    !activeStageObj
  ) {
    return <FlowLoading />;
  }

  if (!gestor && !loading) {
    return (
      <div className="mx-auto container p-4 lg:p-10">
        <div className="flex flex-row items-center space-x-3 mb-16">
          <div>
            <Link to="../" tabIndex={-1}>
              <button className="rounded-full bg-purple flex justify-center items-center p-1">
                <RiArrowLeftSLine size={28} className="text-white" />
              </button>
            </Link>
          </div>
          <h1 tabIndex={0} className="text-lg lg:text-3xl py-1 rounded-full">
            Minha Equipe
          </h1>
        </div>

        <div className="border-2 border-electric-blue rounded-3xl p-8 bg-oxford-blue">
          <p tabIndex={0} className="text-dandelion text-lg font-semibold block text-center">
            Você não possui acesso à esta página.
          </p>
        </div>
      </div>
    );
  }

  if (user?.extra.rede) return <NotFound />;

  // Expressões que o JSX antigo avaliava inline, extraídas sem mudança para o JSX novo.
  const faseSemRealizacoes = (activeStageObj as any)?._id === '68af4da8458e527eaa788503';

  const mostraCartaoDaFase =
    (activeStageObj as any)?.status === 'open' ||
    (getActiveEvaluationStep &&
      ((getActiveEvaluationStep === 5 && (activeStageObj as any)?.type && (activeStageObj as any)?.type === 'semester') ||
        (getActiveEvaluationStep >= 4 &&
          getActiveEvaluationStep < 5 &&
          (activeStageObj as any)?.type &&
          (activeStageObj as any)?.type === 'trimester') ||
        (getActiveEvaluationStep < 4 && (activeStageObj as any)?.type && (activeStageObj as any)?.type === 'month'))) ||
    (isDirectManager &&
      (madUserCodEtapa(baseCtx, userInfo) === 1 ||
        madUserCodEtapa(baseCtx, userInfo) === 3 ||
        madUserCodEtapa(baseCtx, userInfo) === 4 ||
        madUserCodEtapa(baseCtx, userInfo) === 5));

  const mostraAbasForaDaJanela = !!hasRedeMAD?.mad || !!hasRedeMAD?.rede || canSwitchFlow;

  const mostraContadores =
    !!(
      getActiveEvaluationStep ||
      (isDirectManager &&
        (madUserCodEtapa(baseCtx, userInfo) === 1 ||
          madUserCodEtapa(baseCtx, userInfo) === 3 ||
          madUserCodEtapa(baseCtx, userInfo) === 4 ||
          madUserCodEtapa(baseCtx, userInfo) === 5))
    ) &&
    !!(
      getActiveEvaluationStep &&
      ((getActiveEvaluationStep === 5 &&
        madUserCodEtapa(baseCtx, userInfo) === 5 &&
        (activeStageObj as any)?.type &&
        (activeStageObj as any)?.type === 'semester') ||
        ((getActiveEvaluationStep === 4 || getActiveEvaluationStep === 4.1) &&
          (madUserCodEtapa(baseCtx, userInfo) === 4 || madUserCodEtapa(baseCtx, userInfo) === 5) &&
          (activeStageObj as any)?.type &&
          (activeStageObj as any)?.type === 'trimester') ||
        (getActiveEvaluationStep < 4 &&
          (madUserCodEtapa(baseCtx, userInfo) as number) < 4 &&
          (activeStageObj as any)?.type &&
          (activeStageObj as any)?.type === 'month') ||
        (isDirectManager &&
          (madUserCodEtapa(baseCtx, userInfo) === 1 ||
            madUserCodEtapa(baseCtx, userInfo) === 3 ||
            madUserCodEtapa(baseCtx, userInfo) === 4 ||
            madUserCodEtapa(baseCtx, userInfo) === 5)))
    );

  const faseComJanela = !loading && !faseSemRealizacoes;

  const filtroDeFeedbackAplicado =
    typeof selectedFeedbackStatus === 'string' &&
    selectedFeedbackStatus.length &&
    selectedFeedbackStatus.toLowerCase() !== 'selecione';

  const filtroDeStatusAplicado =
    typeof selectedtasksStatus === 'string' && selectedtasksStatus.length && selectedtasksStatus.toLowerCase() !== 'selecione';

  // Regra do vazio por filtro da página antiga (o filtro de status não entra nela).
  const vazioPorFiltro = !!(
    Array.isArray(filteredData) &&
    filteredData.length &&
    (searchText?.length || filtroDeFeedbackAplicado)
  );

  function trocarDeCiclo(valor: string) {
    setActiveCycleSelected(valor);
    setCurrentCycle(undefined);
    setCurrentStage(undefined);

    const selected = cyclesExtended?.find(x => x._id === valor);

    if (selected) {
      setCycleSelected({
        _id: selected._id,
        title: selected.title,
        version: GetVersion(selected.date_start),
      });
    }
  }

  function limparFiltros() {
    setSearchText(undefined);
    setSelectedFeedbackStatus(undefined);
    setSelectedTasksStatus(undefined);
  }

  // Cartão que corresponde a uma opção dos filtros atuais vira botão desse filtro (premissa "Indicadores da fila").
  function filtrarPorStatus(status: string): Partial<IndicadorDaFila> {
    if (!taskStatusOptions.some(option => option.value === status)) return {};
    const ativo = selectedtasksStatus === status;
    return { ativo, onFiltrar: () => setSelectedTasksStatus(ativo ? undefined : status) };
  }

  function filtrarPorFeedback(valor: string): Partial<IndicadorDaFila> {
    const ativo = selectedFeedbackStatus === valor;
    return { ativo, onFiltrar: () => setSelectedFeedbackStatus(ativo ? undefined : valor) };
  }

  // Contadores da página antiga, nas mesmas condições e com os mesmos valores de `quotas` (RDN-48).
  function indicadoresDaFase(): IndicadorDaFila[] {
    const indicadores: IndicadorDaFila[] = [];

    if (!!isOriginalManager) {
      if (!!quotas.maxValidTasks) {
        indicadores.push({
          chave: 'validadas',
          rotulo: 'Total de realizações validadas',
          valor: `${quotas.validTasks?.length || 0}/${quotas.maxValidTasks || 0}`,
          tom: 'verde',
        });
      }

      if (!!quotas.maxRejectedTasks) {
        indicadores.push({
          chave: 'devolvidas',
          rotulo: 'Total de realizações devolvidas',
          valor: `${quotas.rejectedTasks?.length || 0}/${quotas.maxRejectedTasks || 0}`,
          tom: 'rosa',
          ...filtrarPorStatus('Devolvida'),
        });
      }
    }

    if (
      !!isOriginalManagerCod5 ||
      (madUserCodEtapa(baseCtx) &&
        madUserCodEtapa(baseCtx) !== 2 &&
        (madUserCodEtapa(baseCtx) as number) < 5 &&
        getActiveEvaluationStep &&
        getActiveEvaluationStep > 2) ||
      (madUserCodEtapa(baseCtx) && madUserCodEtapa(baseCtx) !== 2 && (madUserCodEtapa(baseCtx) as number) < 5 && isDirectManager)
    ) {
      if (!!quotas.maxDoubledTasks) {
        indicadores.push({
          chave: 'bonificadas',
          // RDV-30 (AD-039): rótulo do design; valor e regra de hoje.
          rotulo: 'Bonificadas por você',
          valor: `${quotas.doubledTasks?.length || 0}/${quotas.maxDoubledTasks || 0}`,
          tom: 'amarelo',
        });
      }

      if (madUserCodEtapa(baseCtx) !== 5 && !!quotas.maxPromotedTasks) {
        indicadores.push({
          chave: 'promovidas',
          // RDV-30 (AD-039): rótulo do design sem o "· cota {pct}%": o percentual da cota fica dentro de
          // `computeQuotas` e a tela não o recebe; valor e regra de hoje.
          rotulo:
            madUserCodEtapa(baseCtx) === 5 &&
            (getActiveEvaluationStep === 5 ||
              playerInfo.funcao_localizacao === 3001 ||
              playerInfo.funcao_localizacao === 3002 ||
              !isOriginalManager)
              ? 'Total de selos concedidos'
              : 'Promovidas por você',
          valor: `${quotas.promotedTasks?.length || 0}/${quotas.maxPromotedTasks || 0}`,
          tom: 'azul',
        });
      }
    }

    if (tasksProsseguirAssessor.length) {
      const enviadas = tasksProsseguirAssessor.filter(item => {
        return (
          // Aprovação vale para a DUPLA (PIT-05/COT-11): quem aprovou pode
          // ter sido o parceiro — o card é da dupla, não do indivíduo.
          (item.prosseguir_who_approved &&
            (item.prosseguir_who_approved === internalUser?.extra.id ||
              baseCtx.duplaIds.includes(String(item.prosseguir_who_approved)))) ||
          (Array.isArray(item.prosseguir_managers) &&
            item.prosseguir_managers.length >= 2 &&
            item.prosseguir_managers[0] === playerInfo._id)
        );
      }).length;

      indicadores.push({
        chave: 'enviadas-diretoria',
        rotulo: 'Total de realizações enviadas para apreciação da diretoria',
        valor: `${enviadas}/${tasksProsseguirAssessor.length}`,
        tom: 'ciano',
      });
    }

    if (madUserCodEtapa(baseCtx) === 5 && (activeStageObj as any)?.type && (activeStageObj as any)?.type === 'semester') {
      indicadores.push(
        {
          chave: 'valeu',
          rotulo: 'Total de realizações Valeu',
          valor: `${quotas.valeuTasks?.length || 0}/${quotas.maxValeuTasks || 0}`,
          tom: 'amarelo',
        },
        {
          chave: 'mandou-bem',
          rotulo: 'Total de realizações Mandou Bem',
          valor: `${quotas.mandouBemTasks?.length || 0}/${quotas.maxMandouBemTasks || 0}`,
          tom: 'amarelo',
        },
        {
          chave: 'uau',
          rotulo: 'Total de realizações Uau',
          valor: `${quotas.uauTasks?.length || 0}/${quotas.maxUauTasks || 0}`,
          tom: 'amarelo',
        },
      );
    }

    indicadores.push({
      chave: 'feedbacks',
      // RDV-30: "Com feedback" com a contagem da opção "Com feedback (n)" do filtro sobre o total da lista da fase.
      rotulo: 'Com feedback',
      valor: `${contagemDeFeedback.comFeedback}/${contagemDeFeedback.total}`,
      tom: 'ciano',
      ...filtrarPorFeedback('sim'),
    });

    return indicadores;
  }

  const cicloEmExibicao = optionsCycle.find(x => x.value === cycleSelected?._id)
    ? optionsCycle.find(x => x.value === cycleSelected?._id)
    : optionsCycle[getIndexByValue(optionsCycle, activeCycleSelected)];

  return (
    <>
      {!!pageLoading && (
        <div className="fixed z-40 w-full h-full top-0 left-0 block flex items-center justify-center bg-black bg-opacity-60">
          <div role="status" className="flex items-center justify-center flex-col space-y-2 text-white">
            <svg
              aria-hidden="true"
              className="w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span tabIndex={0} className="text-white">
              Carregando...
            </span>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-[1000px] px-4 pb-6 leading-[normal] lg:px-0" aria-busy={loading}>
        <TopoDoGestor
          pendentes={eligibility?.madPending}
          fase={activeStageObj}
          etapaAtiva={getActiveEvaluationStep}
          inicioDaApreciacao={
            faseComJanela && mostraCartaoDaFase && activeStageObj && (activeStageObj as any).status === 'open'
              ? (activeStageObj as any).date_finish
              : undefined
          }
        />

        <SeletoresDaFila
          ciclos={optionsCycle}
          ciclo={cicloEmExibicao?.value ?? ''}
          onCiclo={trocarDeCiclo}
          fases={stages ?? []}
          fase={currentStage}
          onFase={setCurrentStage}
          desabilitado={loading}
          lateralidade={
            !!isVacationManager && !!internalUser
              ? {
                  verbo: madUserCodEtapa(baseCtx) && madUserCodEtapa(baseCtx) === 2 ? 'validando' : 'apreciando',
                  nome: internalUser.name,
                  detalhe: `${internalUser._id} · ${internalUser.extra?.dependencia} - ${internalUser.extra?.funcao}`,
                  onSairKeyDown: handleLinkSpaceKeyDown,
                }
              : undefined
          }
          listas={
            faseComJanela && (mostraCartaoDaFase || mostraAbasForaDaJanela) ? (
              <FlowTabs active="mad" eligibility={eligibility} hasRede={!!hasRedeMAD?.rede} currentStage={currentStage} />
            ) : undefined
          }
          sublistas={
            faseComJanela && (mostraCartaoDaFase || mostraAbasForaDaJanela) && pairOptions.length > 1
              ? { opcoes: pairOptions, valor: pairSelected, onEscolher: setPairSelected }
              : undefined
          }
        />


        <SecaoDaFila
          rotuloDaFase={!loading && filteredData ? `Fase ${getMonthNameFromString((activeStageObj as any)?.title)}` : undefined}
          contagem={
            !loading && filteredData ? { naLista: tasksByFilter?.length ?? 0, noEscopo: filteredData.length } : undefined
          }
          exportar={
            tasksByFilter && tasksByFilter?.length > 0 ? (
              <ExportButton data={tasksByFilter} className={CLASSES_DO_EXPORTAR} />
            ) : undefined
          }
          indicadores={faseComJanela && mostraCartaoDaFase && mostraContadores ? indicadoresDaFase() : []}
          filtros={
            !loading && filteredData && filteredData?.length > 0
              ? {
                  busca: searchText || '',
                  onBusca: setSearchText,
                  mostrarSeletores: Array.isArray(tasksStatus) && !!tasksStatus.length,
                  opcoesDeFeedback: feedbackFilterOptions,
                  feedback: selectedFeedbackStatus,
                  onFeedback: setSelectedFeedbackStatus,
                  opcoesDeStatus: taskStatusOptions,
                  status: selectedtasksStatus,
                  onStatus: setSelectedTasksStatus,
                  ordem,
                  onOrdem: setOrdem,
                  filtrosAtivos: !!searchText?.length || !!filtroDeFeedbackAplicado || !!filtroDeStatusAplicado,
                  onLimparFiltros: limparFiltros,
                }
              : undefined
          }
          orientacoes={clarifyingText}
          pitStop={!loading && getActiveEvaluationStep === 4.1 ? periodoDa('Pit Stop', activeStageObj) ?? null : undefined}
        >
          {loading ? (
            <CartoesCarregando />
          ) : tasksByFilter?.length ? (
            ordenarFila(tasksByFilter, ordem).map(item => (
              <CartaoDaFila
                variante="mad"
                key={item._id}
                editable={canEdit(ctx, item)}
                bonus={
                  !!bonus?.hasBonus &&
                  !bonus?.isUsed &&
                  quotas.promotedTasks.length >= quotas.maxPromotedTasks &&
                  (!Array.isArray(item.prosseguir_managers) ||
                    !item.prosseguir_managers.length ||
                    !item.prosseguir_managers.includes(playerInfo._id))
                }
                promote={canPromote(ctx, item, item)}
                double={canDouble(ctx, item)}
                award={canAward(ctx, item)}
                onClearStatus={(item: any, status: any) => console.log('remove status', status, item)}
                data={item}
                manager={gestor}
                managerType={CheckManagerType({
                  user: { ...playerInfo, cod_etapa: madUserCodEtapa(ctx, playerInfo) },
                  item,
                })}
                playerInfo={playerInfo}
                canEvaluate={canEvaluate(ctx, item)}
                canUpdateIndicator={canUpdateIndicator(item, playerInfo)}
                onUpdateIndicator={() => setIndicatorTask({ id: item._id, owner: item.owner })}
                isParticipantsApprover={canApproveParticipantsCount(playerInfo, item)}
                isPitStopAssessor={isPitStopAssessor(ctx, item)}
                onPromote={handlePromote}
                onAccept={handleAccept}
                onReject={handleReject}
                onDouble={handleDoubled}
                onStop={handleStop}
                onFeedback={() => loadData(item._id)}
                onReload={() => loadData(item._id)}
                onAward={handleAward}
                cycle={activeStageObj}
                activeEvaluationStep={getActiveEvaluationStep}
                taskStep={getTaskStepLocal(item)}
                playerStep={getPlayerStep(ctx, item)}
                onReset={handleReset}
                reset={canReset(ctx, item)}
                getAction={getAction}
                clearGetAction={() => setGetAction(null)}
              />
            ))
          ) : (
            <VazioDaFila haviaRealizacoes={!faseSemRealizacoes && vazioPorFiltro} onLimparFiltros={limparFiltros} />
          )}
        </SecaoDaFila>

        {!!getFullCalendar && !faseSemRealizacoes && <CalendarioDoCiclo etapas={etapasDoCiclo(cycles)} agora={realDatetime} />}
      </div>

      <Outlet />

      {!!selectedDupla && (
        <SelectDupla
          player={selectedDupla}
          closeModal={() => {
            setSelectedDupla(undefined);
          }}
          onSubmit={option => {
            const task = selectedDupla.task;
            setSelectedDupla(undefined);

            const selection = resolveDuplaSelection(option);
            if (selection) {
              functionsObject[functionDupla](task, selection);
            }
          }}
        />
      )}

      {!!modalCod5?._id && (
        <ModalCod5
          task={modalCod5}
          playerInfo={playerInfo}
          onSubmit={handleSubmitCod5}
          onClose={() => setModalCod5(undefined)}
          award={canAward(ctx, modalCod5, true)}
          userCodEtapa={madUserCodEtapa(ctx)}
        />
      )}

      {!!showConcurrentActionModal && (
        <ModalAlert
          description={showConcurrentActionModal}
          onClose={() => {
            setShowConcurrentActionModal(undefined);
          }}
        />
      )}

      {!!indicatorTask && (
        <ModalChangeIndicator
          id={indicatorTask.id}
          owner={indicatorTask.owner}
          callback={updated => {
            const taskId = indicatorTask.id;
            setIndicatorTask(undefined);

            if (updated) loadData(taskId);
          }}
        />
      )}
    </>
  );
}
